import { trackById } from 'src/app/shared/utils/track-by.util';
import { openFileInNewTab } from 'src/app/shared/utils/file-link.util';
import { DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS, downloadBlob } from 'src/app/shared/utils/download.util';
import { LabResultImportService, labResultTemplateFileName } from 'src/app/modals/lab-result-import-dialog/lab-result-import.service';
import { Component, Inject, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { IntegrationType } from 'src/app/enums/IntegrationType.enum';
import { SampleStatusColors, SampleStatusEnum } from 'src/app/enums/sampleStatus.enum';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { Lab, TAT } from 'src/app/models/lab.model';
import { Lookup } from 'src/app/models/lookup.model';
import { Sample } from 'src/app/models/sample.model';
import { LabRequisitionService } from 'src/app/services/lab-requisition.service';
import { LabService } from 'src/app/services/lab.service';
import { SampleService } from 'src/app/services/sample.service';
import { ToastService } from 'src/app/services/toast.service';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { StepConfig, StepperConfig } from 'src/app/shared/stepper/stepper.model';
import { EventService } from 'src/app/services/event.service';
import { EventKeys } from 'src/app/shared/constants/eventKeys.constants';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { LabRequisitionStatus } from 'src/app/enums/labRequisitionStatus.enum';
import { LabRequisition } from 'src/app/models/labRequisition.model';
import { Router } from '@angular/router';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { FileAttachmentService } from 'src/app/services/file-attachment.service';
import { UploadService } from 'src/app/services/upload.service';
import { generateGUID } from 'src/app/_helpers/guid.helper';
import { AssetTypeEnum } from 'src/app/enums/assetType.enum';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { CreatePayload } from 'src/app/shared/grid/crud.service';
import { FileTypeEnum } from 'src/app/enums/fileType.enum';
import { DocumentType } from 'src/app/enums/documentType.enum';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { AuthService } from 'src/app/services';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { PdfGeneratorService } from 'src/app/components/report-generation-popup/services/pdf-generator.service';
import { PdfGeneratedData } from 'src/app/models/pdfGeneratedData.model';
import { ChainOfCustodyPdfService } from './chain-of-custody-pdf.service';
import { ChainOfCustodyReportEntity } from 'src/app/services/template-mappers/entities/chain-of-custody-template-mapper.model';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { buildCocBarcodeContent } from 'src/app/shared/utils/coc-barcode.util';
import { TranslateService } from 'src/app/services/translate.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { ParameterTypeEnum } from 'src/app/enums/parameterType.enum';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridView } from 'src/app/models/gridView.model';
import { getSampleGridIdForLab } from 'src/app/shared/constants/gridIds.constants';

export interface IGenerateCocPopup extends IFormDialogData {
  samples?: Sample[];
  isFromSamples?: boolean;
}

interface SampleVm extends Sample {
    agentNamesStr: string;
    samplingDateStr: string;
    volumeLitersStr: string;
    runTimeMinutesStr: string;
    flowRateStr: string;
}

@Component({
  standalone: false,
  selector: 'app-generate-coc-popup',
  templateUrl: './generate-coc-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ChainOfCustodyPdfService, PdfGeneratorService],
  styleUrl: './generate-coc-popup.component.scss'
})
export class GenerateCocPopupComponent implements OnInit {
  readonly IntegrationType = IntegrationType;
  @ViewChild('account') accountTpl!: TemplateRef<any>;
  @ViewChild('accountInfo') accountInfoTpl!: TemplateRef<any>;
  @ViewChild('alsQuote') alsQuoteTpl!: TemplateRef<any>;
  @ViewChild('tat') tatTpl!: TemplateRef<any>;
  @ViewChild('collection') collectionTpl!: TemplateRef<any>;
  @ViewChild('sampling') samplingTpl!: TemplateRef<any>;
  @ViewChild('finalize') finalizeTpl!: TemplateRef<any>;
  stepperConfig!: StepperConfig;
  labDetails: Lab;
  shownSampleDetails: {
    siteNames: string[];
    sampledby: string[];
    surveyNames: string[];
    jurisdictionNames: string[];
  }

  stateOptions = [
    { value: 'state1', label: 'State 1' },
    { value: 'state2', label: 'State 2' },
  ];
  samplesList: SampleVm[] = [];
  selectedSamples: SampleVm[] = [];
  sampleColumns: string[] = [];
  tatColumns = [];
  selectedLabId: string;
  labsList: Lookup[] = [];
  sampleStatusDetails = {
    label: getNamedEnum(SampleStatusEnum)[SampleStatusEnum.AwaitingLabSubmission],
    color: SampleStatusColors[SampleStatusEnum.AwaitingLabSubmission]
  }
  labRequisitionFields: LabRequisition;
  exemptedSamples: Sample[] = [];
  blankCount = 0;
  totalSampleCount = 0;
  blankRuleBelowCount = false;
  blankRuleBelowRatio = false;
  blankRuleViolated = false;
  effectiveMinBlankCount = 2;
  effectiveMinBlankRatioPercent = 10;
  relinquishedBySignature = '';
  relinquishedByDate = '';
  relinquishedByTime = '';

  private readonly DEFAULT_MIN_BLANK_COUNT = 2;          // mirrors BE Constants
  private readonly DEFAULT_MIN_BLANK_RATIO_PERCENT = 10; // client-side warning only — server authoritative
  isFinalStep: boolean;
  showCustomizeTemplate = false;
  canCustomizeTemplate = false;
  reportEntity: ChainOfCustodyReportEntity;
  readonly chainOfCustodyEntityType = ENTITY_TYPES.CHAIN_OF_CUSTODY;
  loading: boolean;
  userDateWithTimeFormat: string;
  tenantLogoUrl: string;
  alternateSampleLabel: string;
  flowRateUnit: string;
  private readonly alternateSampleColumn = nameOf<Sample>(x => x.alternateSampleNumber);
  readonly defaultSampleColumns: string[] = [
    nameOf<Sample>(x => x.id),
    nameOf<Sample>(x => x.sampleNumber),
    nameOf<IhLabResult>(x => x.masterAgentName),
    nameOf<IhLabResult>(x => x.agentGroupId),
    nameOf<Sample>(x => x.startDate),
    nameOf<Sample>(x => x.sampleVolume),
    nameOf<Sample>(x => x.runTime),
    nameOf<Sample>(x => x.flowRate),
    nameOf<Sample>(x => x.analyticalMethodName),
  ];
  constructor(@Inject(FLYOUT_DATA) public data: IGenerateCocPopup,
    private labService: LabService,
    private sampleService: SampleService,
    private labRequisitionService: LabRequisitionService,
    private toastService: ToastService,
    private eventService: EventService,
    private router: Router,
    private dateFormatService: DateFormatService,
    private agentGroupService: AgentGroupService,
    private uploadService: UploadService,
    private fileAttachmentService: FileAttachmentService,
    private organizationLevelService: OrganizationLevelService,
    private authService: AuthService,
    private cocPdf: ChainOfCustodyPdfService,
    private translate: TranslateService,
    private gridViewService: GridViewService,
    private labResultImportService: LabResultImportService,
    private valueConversionService: ValueConversionService) {
    this.sampleColumns = [...this.defaultSampleColumns];
    this.tatColumns = [
      nameOf<TAT>(x => x.turnAroundTime),
      nameOf<TAT>(x => x.surcharge),
    ]
    this.labRequisitionFields = new LabRequisition();
  }

  ngOnInit(): void {
    this.loading = true;
    const { id, isFromSamples } = this.data;
    this.userDateWithTimeFormat = this.dateFormatService.userDateWithTimeFormat;
    this.flowRateUnit = this.valueConversionService.getParameterUnit(ParameterTypeEnum.FlowRate);

    forkJoin({
      labRequisitionData: id
        ? this.labRequisitionService.getById(id)
        : of({} as LabRequisition),
      sampleFieldsConfig: this.sampleService.getFieldConfiguration(),
      labList: id ? of([]) : this.labService.getAll()
    }).subscribe({
      next: async (result) => {

        this.labsList = result.labList;
        this.labRequisitionFields = result.labRequisitionData;

        this.alternateSampleLabel =
          result.sampleFieldsConfig['alternateSampleNumber']?.displayName ||
          this.translate.getDynamicTranslation('Alternate Sample Number');

        let samples: Sample[] = [];

        if (id && this.labRequisitionFields?.sampleIds?.length) {

          // Fetch samples directly by saved IDs
          const sampleRequests = this.labRequisitionFields.sampleIds.map(sampleId =>
            this.sampleService.getById(sampleId)
          );

          samples = await firstValueFrom(forkJoin(sampleRequests));

        } else if (isFromSamples) {

          samples = this.data.samples || [];

        } else {

          const sampleFilter = JSON.stringify({
            sampleStatus: SampleStatusEnum.AwaitingLabSubmission
          });

          const response = await firstValueFrom(
            this.sampleService.get('', 'id', 'asc', 0, 100, sampleFilter)
          );

          samples = response.data;
        }

        this.samplesList = samples.map(s => this.toSampleVm(s));

        if (id) {
          this.selectedLabId = this.labRequisitionFields.labId;

          this.data.samples = samples.filter(s =>
            this.labRequisitionFields.sampleIds.includes(s.id)
          );

          this.selectedSamples = this.samplesList.filter(
            s => s.sampleStatus === SampleStatusEnum.AwaitingLabSubmission &&
              this.labRequisitionFields.sampleIds.includes(s.id)
          );

          this.showSampleSelectionTable();
        }

        this.loading = false;
      },
      error: (error) => {
        return this.toastService.showError(error);
      }
    });
    const currentUser = this.authService.getCurrentUser();
    this.tenantLogoUrl = currentUser?.tenantLogoUrl;
    this.canCustomizeTemplate = [SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser?.userType);
    // 3-line block: full name, email, then the "Signed by LEAP" system label. \n-joined for the PDF cell.
    this.relinquishedBySignature = [
      currentUser?.userFullName ?? '',
      currentUser?.userEmail ?? '',
      this.translate.getDynamicTranslation('Signed by LEAP')
    ].filter(Boolean).join('\n');
    this.relinquishedByDate = this.dateFormatService.formatDateTimeToString(new Date(), this.dateFormatService.userDateFormat);
    this.relinquishedByTime = this.dateFormatService.formatDateTimeToString(new Date(), this.dateFormatService.userTimeFormat);
  }

  private computeBlankRuleState(): void {
    this.totalSampleCount = this.selectedSamples.length;
    this.blankCount = this.selectedSamples.filter(
      s => s.sampleCategory === SampleCategoryEnum.Blank
    ).length;

    const enforceBlankRules = this.labDetails?.enforceBlankRules ?? false;
    this.effectiveMinBlankCount = this.labDetails?.minBlankCount ?? this.DEFAULT_MIN_BLANK_COUNT;
    this.effectiveMinBlankRatioPercent = this.labDetails?.minBlankRatioPercent ?? this.DEFAULT_MIN_BLANK_RATIO_PERCENT;

    this.blankRuleBelowCount = enforceBlankRules && this.blankCount < this.effectiveMinBlankCount;
    this.blankRuleBelowRatio = enforceBlankRules && this.totalSampleCount > 0
      && (this.blankCount * 100) < (this.effectiveMinBlankRatioPercent * this.totalSampleCount);
    this.blankRuleViolated = this.blankRuleBelowCount || this.blankRuleBelowRatio;
  }

  setStepper() {
    if (this.data.samples) {
      this.exemptedSamples = this.data.samples.filter(sample => sample.sampleStatus !== SampleStatusEnum.AwaitingLabSubmission);
    }
    this.computeBlankRuleState();

    this.stepperConfig = {
      orientation: 'horizontal',
      linear: true,
      steps: [
        {
          id: 'sampling', label: 'Sampling', template: this.samplingTpl,
          canProceed: () => {
            if (!this.selectedSamples.length) this.toastService.displayErrorMessage("Please select at least one sample", true);
            if (this.selectedSamples.length > 0 && this.labDetails.labIntegrationType === IntegrationType.Lims) {
              this.generateBarCode();
            }
            if (this.selectedSamples.length) this.setParentSites();
            return this.selectedSamples.length > 0;
          }
        },
        {
          id: 'account', label: 'Account', template: this.accountTpl,
          visible: () => this.labDetails.labIntegrationType === IntegrationType.Lims,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ],
          canProceed: () => {
            const num = this.labRequisitionFields.labRequisitionNumber;
            if (!num) return this.toastService.displayErrorMessage("Please enter a Lab Requisition Number", true), false;
            if (num.length > 20) return this.toastService.displayErrorMessage("Lab Requisition Number cannot exceed 20 characters", true), false;
            return true;
          }
        },
        {
          id: 'accountInfo', label: 'Account Information', template: this.accountInfoTpl,
          visible: () => this.labDetails.labIntegrationType === IntegrationType.GalsonLab,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ],
          canProceed: () => {
            if (!this.labRequisitionFields.labRequisitionNumber) this.toastService.displayErrorMessage("Please enter a Lab Requisition Number", true);
            return Boolean(this.labRequisitionFields.labRequisitionNumber);
          }
        },
        {
          // Label is hardcoded to match all other steps in this component; component-wide i18n of step labels is out of scope
          id: 'alsQuote', label: 'ALS Quote', template: this.alsQuoteTpl,
          visible: () => this.labDetails.labIntegrationType === IntegrationType.AlsLab,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ],
          canProceed: () => {
            const num = this.labRequisitionFields.labRequisitionNumber;
            if (!num) return this.toastService.displayErrorMessage("Please enter a Lab Requisition Number", true), false;
            if (num.length > 20) return this.toastService.displayErrorMessage("Lab Requisition Number cannot exceed 20 characters", true), false;
            return true;
          }
        },
        {
          id: 'tat', label: 'TAT', template: this.tatTpl,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ]
        },
        {
          id: 'collection', label: 'Collection', template: this.collectionTpl,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ]
        },
        {
          id: 'finalize', label: 'Finalize', template: this.finalizeTpl,
          buttons: [
            { label: 'Save Progress & View PDF', action: () => this.saveProgress(true) },
            {
              label: 'Finalize COC and Print',
              disabled: () => this.blankRuleViolated,
              action: () => {
                if (this.blankRuleViolated) {
                  this.toastService.displayErrorMessage(
                    this.blankRuleBelowCount
                      ? 'This Lab Requisition requires at least {{count}} blank sample(s) but has {{actual}}.'
                      : 'Blank samples must be at least {{ratio}}% of samples on this requisition.',
                    true,
                    this.blankRuleBelowCount
                      ? { count: this.effectiveMinBlankCount, actual: this.blankCount }
                      : { ratio: this.effectiveMinBlankRatioPercent }
                  );
                  return;
                }
                this.loading = true;
                this.saveProgress(true, true);
              }
            }
          ]
        },
      ]
    };
    this.labRequisitionFields = {
      ...this.labRequisitionFields,
      sampleIds: this.selectedSamples.map(sample => sample.id)
    }
  }

  generateBarCode() {
    const agentGroupIds = [
      ...new Set(this.selectedSamples
        .flatMap(s => s.ihLabResults?.map(r => r.agentGroupId) || [])
        .filter(Boolean))];
    if (agentGroupIds.length) {
      this.agentGroupService.getByIds(agentGroupIds)
        .subscribe(agentGroups => {
          const agentGroupMap = new Map(agentGroups.map(agentGroup => [agentGroup.id, agentGroup]));
          this.selectedSamples.forEach(sample => {
            const firstResult = sample.ihLabResults?.[0];
            if (firstResult && agentGroupMap.has(firstResult.agentGroupId)) {
              sample.barcodeContent = buildCocBarcodeContent(agentGroupMap.get(firstResult.agentGroupId)?.casId, sample.startDate as string, sample.sampleNumber);
            } else {
              sample.barcodeContent = undefined;
            }
          });
        });
    }
  }

  showSampleSelectionTable() {
    forkJoin({
      lab: this.labService.getById(this.selectedLabId),
      gridView: this.gridViewService.getDefaultView(getSampleGridIdForLab(this.selectedLabId)).pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ lab, gridView }) => {
        this.sampleColumns = [...this.defaultSampleColumns];
        if (lab.labIntegrationType === IntegrationType.GalsonLab && !this.sampleColumns.includes(this.alternateSampleColumn)) {
          this.sampleColumns.splice(2, 0, this.alternateSampleColumn);
        }
        this.labDetails = lab;
        this.patchFormData();
        this.setStepper();
        if (this.data.id && lab.labIntegrationType === IntegrationType.Lims && this.selectedSamples.length > 0) {
          this.generateBarCode();
        }
        this.getlabOrgs();
        if (gridView?.settingsJson?.columns?.length > 0) {
          this.applyGridViewColumns(gridView);
        }
      },
      error: (error) => {
        this.loading = false;
        this.toastService.showError(error);
      }
    });
  }

  // Any lab can tick Alternate Sample Number in its sample grid settings; only Galson shows it by default.
  private applyGridViewColumns(view: GridView): void {
    const knownColumns = new Set([...this.sampleColumns, this.alternateSampleColumn]);
    const visibleColumns = view.settingsJson.columns
      .filter(col => col.visible && knownColumns.has(col.property))
      .sort((a, b) => a.order - b.order)
      .map(col => col.property);
    if (!visibleColumns.length) return;
    const idProp = nameOf<Sample>(x => x.id);
    if (!visibleColumns.includes(idProp)) {
      visibleColumns.unshift(idProp);
    }
    this.sampleColumns = visibleColumns;
  }

  patchFormData() {
    this.loading = false;
    this.labRequisitionFields = {
      ...this.labDetails, ...this.labRequisitionFields,
      labId: this.labDetails.id, id: this.data.id
    };
    this.updateShownSampleDetails();
  }

  getlabOrgs() {
    const labReqNumber = this.labRequisitionFields.labRequisitionNumber;
    const orgLevelId = this.labRequisitionFields.organizationLevelId ?? this.labDetails?.organizationLevelId;
    if (orgLevelId) {
      const applyRequisionNumber = (org: OrganizationLevel) => {
        if (org.labRequisitionNumberTemplate) {
          if (!labReqNumber) this.labRequisitionFields.labRequisitionNumber = "Autogenerated";
          this.labRequisitionFields.isAutogenerated = true;
        }
      };

      this.organizationLevelService.getById(orgLevelId).subscribe({
        next: (site) => applyRequisionNumber(site),
        error: (err) => {
          this.toastService.showError(err);
        }
      });
    }
  }


  isSelected(row: Sample): boolean {
    return this.selectedSamples.map(sample => sample.id).includes(row.id);
  }

  toggleSelection(row: any, checked: boolean) {
    if (checked) {
      this.selectedSamples.push(row);
    } else {
      this.selectedSamples = this.selectedSamples.filter(s => s.id !== row.id);
    }
    this.updateShownSampleDetails();
  }

  getUniqueAgentNames(ihLabResults: IhLabResult[]): string {
    return this.joinAgentNames(ihLabResults) || '-';
  }

  // The tenant agent name comes back in the user's language; the master agent name is English only.
  private joinAgentNames(ihLabResults: IhLabResult[] | undefined): string {
    return [...new Set((ihLabResults ?? []).map(r => r.agentName || r.masterAgentName).filter(Boolean))].join(', ');
  }

  isAllSelected(): boolean {
    return this.samplesList.length > 0 && this.selectedSamples.length === this.samplesList.length;
  }

  toggleAll(checked: boolean) {
    if (checked) {
      this.selectedSamples = [...this.samplesList];
    } else { this.selectedSamples = []; }
    this.updateShownSampleDetails();
  }

  private updateShownSampleDetails() {
    this.labRequisitionFields.sampleIds = this.selectedSamples.map(sample => sample.id);
    this.computeBlankRuleState();
  }

  async setParentSites() {
    const siteIds = Array.from(
      new Set(this.selectedSamples.map(s => s.organizationLevelId).filter(Boolean))
    );

    const siteNameMap = new Map<string, string>();

    const fetches = siteIds.map(async siteId => {
      try {
        const parentSite = await firstValueFrom(this.organizationLevelService.getParentSite(siteId));
        if (parentSite?.name) {
          siteNameMap.set(siteId, parentSite.name);
        }
      } catch (error) {
      }
    });
    await Promise.all(fetches);

    const siteNameSet = new Set(siteNameMap.values());
    const sampledBySet = new Set(this.selectedSamples.map(s => s.sampledByUsername).filter(Boolean));
    const surveySet = new Set(this.selectedSamples.map(s => s.surveyName).filter(Boolean));
    const jurisdictionSet = new Set(this.selectedSamples.flatMap(sample => sample.ihLabResults ?? [])
      .map(result => result.jurisdictionName).filter(Boolean));

    this.shownSampleDetails = {
      siteNames: Array.from(siteNameSet),
      sampledby: Array.from(sampledBySet),
      surveyNames: Array.from(surveySet),
      jurisdictionNames: Array.from(jurisdictionSet)
    };
  }

  selectTat(row: TAT) {
    this.labRequisitionFields.tatId = row.id;
  }

  isTatSelected(row: any): boolean {
    return this.labRequisitionFields.tatId === row.id;
  }

  onStepChanged(step: StepConfig) {
    this.isFinalStep = Boolean(step.id === 'finalize');
  }

  openSampleDetail(item: Sample) {
    window.open(this.router.serializeUrl(
      this.router.createUrlTree([`/dashboard/sample-details/${item.id}`])
    ), '_blank');
  }

  async saveProgress(viewPdf = false, finalizeAndPrint = false) {
    this.loading = true;
    try {
      const draftFields = {
        ...this.labRequisitionFields,
        accountNumber: this.labRequisitionFields.accountNumber ?? this.labRequisitionFields.labAccountNumber,
        tatId: this.labRequisitionFields.tatId || null,
        requisitionStatus: LabRequisitionStatus.Draft
      };

      let request$: Observable<LabRequisition | Object>;
      if (this.labRequisitionFields.id) {
        request$ = this.labRequisitionService.update(draftFields);
      } else {
        // Create must not send `id`: the server assigns the PK and the API's strict Guid
        // JSON binder rejects `id: ''` with a 400 before the action runs. Because `draftFields`
        // is a pre-built variable, TS's excess-property check does not enforce the
        // `CreatePayload<T> = Omit<T, 'id'>` guard, so strip `id` explicitly here.
        const { id, ...createPayload } = draftFields;
        request$ = this.labRequisitionService.create(createPayload);
      }

      const draftRes = await firstValueFrom(request$) as LabRequisition;

      this.labRequisitionFields.id = this.labRequisitionFields.id || draftRes.id;
      this.labRequisitionFields.labRequisitionNumber = draftRes?.labRequisitionNumber || this.labRequisitionFields.labRequisitionNumber

      if (!viewPdf) {
        this.toastService.showSuccess('Progress Saved Successfully', true);
        this.eventService.broadcast(EventKeys.flyoutClose, {
          ...draftFields,
          ...draftRes,
          id: this.labRequisitionFields.id,
          labRequisitionNumber: this.labRequisitionFields.labRequisitionNumber
        });
        return;
      }

      const pdfBlob = await this.cocPdf.render(this.buildReportEntity());

      if (!finalizeAndPrint) {
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS);
        return;
      }

      const fileKey = generateGUID();
      const signedUrl = await firstValueFrom(
        this.uploadService.getAssetsPreSignedURL(fileKey, AssetTypeEnum.IHAttachments)
      );
      await firstValueFrom(this.uploadService.uploadFile(signedUrl, pdfBlob));

      const payload: CreatePayload<FileAttachment> = {
        name: `${fileKey}.pdf`,
        fileKey,
        description: 'coc-pdf',
        organizationLevelId: this.labRequisitionFields.organizationLevelId,
        fileType: FileTypeEnum.Pdf,
        documentType: DocumentType.ChainOfCustody
      };

      const file = await firstValueFrom(this.fileAttachmentService.create(payload));

      const completeFields = {
        ...this.labRequisitionFields,
        fileAttachmentIds: [file.id],
        tatId: this.labRequisitionFields.tatId || null,
        requisitionStatus: LabRequisitionStatus.Completed
      };

      await firstValueFrom(this.labRequisitionService.update(completeFields));

      // DASH-2722: on finalize the user also gets the empty results template alongside the COC PDF. Best-effort —
      // the requisition is already Completed, so a template failure must not roll that back or block the flow.
      await this.downloadResultTemplate();

      this.toastService.showSuccess('Lab Requisition Completed Successfully', true);
      this.eventService.broadcast(EventKeys.flyoutClose, completeFields);

      if (!openFileInNewTab(file?.url)) {
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS);
      }
    } catch (err) {
      this.toastService.showError(err);
    } finally {
      this.loading = false;
    }
  }

  // DASH-2722: regenerate (never persist) the empty lab-result template for the finalized requisition and hand it
  // to the browser. Swallows its own errors so a template hiccup can't undo the already-Completed COC.
  private async downloadResultTemplate(): Promise<void> {
    if (!this.labRequisitionFields.id) return;
    // The template endpoint is Admin-only on the BE (mirrors the import-results gate). A non-Admin request would
    // 403 and fire a spurious error toast right after the just-Completed COC, making a fine COC look broken.
    // Skip the best-effort download for users who cannot call the endpoint.
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || ![SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType)) return;
    try {
      const blob = await firstValueFrom(this.labResultImportService.downloadTemplate(this.labRequisitionFields.id));
      const number = this.labRequisitionFields.labRequisitionNumber || String(this.labRequisitionFields.id);
      downloadBlob(blob, labResultTemplateFileName(number));
    } catch {
      this.toastService.displayErrorMessage('Lab result template could not be generated', true);
    }
  }

  public trackById = trackById;

  formatVolumeLiters(sampleVolume: number | null): string {
    if (sampleVolume == null) return '-';
    return sampleVolume.toFixed(2);
  }

  formatRunTimeMinutes(runTime: number | null): string {
    if (runTime == null) return '-';
    return this.valueConversionService.convertSecondsToMinutes(runTime).toFixed(0);
  }

  formatFlowRate(sampleAverageCal: number | null): string {
    if (sampleAverageCal == null) return '-';
    return sampleAverageCal.toFixed(3);
  }

  formatSamplingDate(startDate: Date | string | number): string {
    if (!startDate) return '-';
    return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(startDate as string, this.userDateWithTimeFormat);
  }

  openCustomizeTemplate(): void {
    this.reportEntity = this.buildReportEntity();
    this.showCustomizeTemplate = true;
  }

  closeCustomizeTemplate(): void {
    this.showCustomizeTemplate = false;
  }

  onCustomizePdfGenerated(event: PdfGeneratedData): void {
    if (event.openInNewTab) {
      const url = URL.createObjectURL(event.blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS);
    }
    event.onComplete?.();
  }

  private buildReportEntity(): ChainOfCustodyReportEntity {
    return { requisition: this.labRequisitionFields, lab: this.labDetails, samples: this.selectedSamples };
  }

  private toSampleVm(sample: Sample): SampleVm {
    return {
      ...sample,
      agentNamesStr: this.getUniqueAgentNames(sample.ihLabResults),
      samplingDateStr: this.formatSamplingDate(sample.startDate),
      volumeLitersStr: this.formatVolumeLiters(sample.sampleVolume),
      runTimeMinutesStr: this.formatRunTimeMinutes(sample.runTime),
      flowRateStr: this.formatFlowRate(sample.sampleAverageCal)
    };
  }

}
